import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import middleware from '../../Api/Middleware';
import ToastError from '../../Toasts/ToastError';
import OpenEmailModal from '../../Modals/Orders/OpenEmailModal';
import TablePagination from '../../Table/TablePagination';
import './OrderEmailHistory.css';

const PAGE_SIZE_OPTIONS = [5, 10];
const DEFAULT_SORT_FIELD = 'created_at';
const DEFAULT_SORT_DIRECTION = 'desc';

const OrderEmailHistory = ({ orderId, refresh }) => {
  const [emails, setEmails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [paginationState, setPaginationState] = useState({
    page_size: PAGE_SIZE_OPTIONS[0],
    page: 1,
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchEmails = useCallback(async () => {
    if (!orderId) {
      setEmails([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await middleware.get(`/orders/emails?order_id=${orderId}`);
      setEmails(response?.data?.data ?? []);
    } catch (error) {
      ToastError(`Error message ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails, refresh]);

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  }, [orderId]);

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleSort = (field) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }
      setSortDirection(DEFAULT_SORT_DIRECTION);
      return field;
    });
  };

  const filteredEmails = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return emails;

    return emails.filter((item) => {
      const subjectMatch = (item.subject || '').toLowerCase().includes(term);
      const createdAtMatch = (item.created_at || '').toString().toLowerCase().includes(term);
      return subjectMatch || createdAtMatch;
    });
  }, [emails, searchTerm]);

  const sortedEmails = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...filteredEmails].sort((a, b) => {
      if (sortField === 'subject') {
        return direction * (a.subject || '').localeCompare(b.subject || '');
      }

      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return direction * (dateA - dateB);
    });
  }, [filteredEmails, sortField, sortDirection]);

  const totalItems = sortedEmails.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / paginationState.page_size);
  const currentPage = Math.min(Math.max(1, paginationState.page), totalPages);
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * paginationState.page_size;
  const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + paginationState.page_size, totalItems);
  const paginatedEmails = sortedEmails.slice(startIndex, endIndex);

  const pagination = {
    page_size: paginationState.page_size,
    page: totalItems === 0 ? 1 : currentPage,
    begin_of_page: totalItems === 0 ? 0 : startIndex + 1,
    end_of_page: endIndex,
    max_items: totalItems,
  };

  const handlePaginationChange = (next) => {
    setPaginationState((prev) => ({
      page_size: next.page_size ?? prev.page_size,
      page: next.page ?? prev.page,
    }));
  };

  const formatDateTime = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    const formattedDate = date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const formattedTime = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <div className="d-flex flex-column" style={{ fontSize: '14px' }}>
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </div>
    );
  }, []);

  const paginationClasses = useMemo(() => ({
    container: 'pagination flex items-center justify-between mt-2 mb-4',
    leftWrapper: 'd-flex gap-2',
    pageSizer: 'page-sizer email-page-sizer',
    pageDisplay: 'page-display text-nowrap me-4',
    rightWrapper: 'pagination-controls d-flex gap-1',
    toFirstLastButton: 'pagination-btn',
    prevNextButton: 'pagination-btn',
    pageNumberButton: 'pagination-btn',
    activePageButton: 'pagination-btn active',
  }), []);

  const paginationIconClasses = useMemo(() => ({
    toFirstLast: '',
    prevNext: '',
  }), []);

  const handleSortKeyDown = (event, fieldKey) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSort(fieldKey);
    }
  };

  const renderSortLabel = (label, fieldKey) => (
    <div
      role="button"
      tabIndex={0}
      className="sort-header"
      onClick={() => handleSort(fieldKey)}
      onKeyDown={(event) => handleSortKeyDown(event, fieldKey)}
    >
      <span>{label}</span>
      <FontAwesomeIcon icon={faSort} className="sort-header-icon" />
    </div>
  );

  return (
    <div className="mt-3">
      <div className="search-bar">
        <input
          type="text"
          placeholder="Zoeken"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="ps-4 search-input rounded-5"
        />
        <div
          className="SearchIcon"
          style={{ width: '42px', height: '42px', position: 'absolute', right: '52px' }}
        />
      </div>
      <div className="email-container pt-2 pb-0 rounded-4 mt-4">
        <table className="w-100" style={{ minHeight: '140px' }}>
          <thead className="bottom-border-line">
            <tr>
              <th className="ps-3">{renderSortLabel('Onderwerp', 'subject')}</th>
              <th className="text-start">{renderSortLabel('Datum verzonden', 'created_at')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={2} className="px-4 py-5 text-center">
                  E-mails worden geladen...
                </td>
              </tr>
            ) : paginatedEmails.length ? (
              paginatedEmails.map((email) => (
                <tr key={email.client_order_email_id}>
                  <td className="ps-3" style={{ fontSize: '14px' }}>
                    <OpenEmailModal
                      order_id={email.order_id || orderId}
                      client_order_email_id={email.client_order_email_id}
                      subject={email.subject || '-'}
                    />
                  </td>
                  <td className="pr-4" style={{ fontSize: '14px' }}>
                    {formatDateTime(email.created_at)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="px-4 py-5 text-center">
                  Er zijn nog geen mails
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        pagination={pagination}
        setPagination={handlePaginationChange}
        pageSizes={PAGE_SIZE_OPTIONS}
        classes={paginationClasses}
        iconClasses={paginationIconClasses}
      />
    </div>
  );
};

export default OrderEmailHistory;
